import * as React from 'react';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, AbsoluteBox, Grid } from '@kis-technologies/kis-ui';
import { NumPadAction } from 'components/NumPad';
import { PhoneNumberInput, Country } from 'components/PhoneNumberInput';
import { selectors as configSelectors, Configuration, Messages } from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { useStyles } from '../styled';
import { ComponentProps } from '../types';

export const DeliveryPhoneCollection = (props: ComponentProps) => {
  const { onNext, onPrev } = props;
  const { t, i18n } = useTranslation();

  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const fontFamily = get(configuration, 'configuration.globalSettings.fonts.primary');
  const messages: Messages = get(
    configuration,
    `${ticketingPath}.ticketDeliverySettings.textMessage.dataCollectionMessages`,
    {}
  );
  const headerText: string = get(messages, `header.${i18n.language}`, t('delivery.whereShouldWeSend'));
  const subheaderText: string = get(messages, `subheader.${i18n.language}`, t('delivery.enterYourPhone'));
  const backButtonText: string = get(messages, `buttons.${i18n.language}[0]`, t('commonText.back'));
  const nextButtonText: string = get(messages, `buttons.${i18n.language}[1]`, t('commonText.continue'));

  const [phoneNumberInput, setPhoneNumberInput] = React.useState<{ keyboard: string; selectedCountry?: Country }>({
    keyboard: ''
  });
  const [phoneNumber, setPhoneNumber] = React.useState<string>('');

  const handleKeyboardClick = (_: React.MouseEvent<HTMLButtonElement>, v: string) => {
    if (v === 'delete') {
      setPhoneNumberInput((prevState) => ({
        ...prevState,
        keyboard: prevState.keyboard.slice(0, prevState.keyboard.length - 1)
      }));
    } else if (v === 'clear') {
      setPhoneNumberInput((prevState) => ({ ...prevState, keyboard: '' }));
    } else {
      setPhoneNumberInput((prevState) => ({ ...prevState, keyboard: prevState.keyboard + v })); // Forward to phone number component
    }
  };

  const handlePhoneNumber = (phone: string, phNumInp: typeof phoneNumberInput) => {
    setPhoneNumber(phone);
    setPhoneNumberInput(phNumInp);
  };

  const isValidPhoneNumber =
    phoneNumberInput.keyboard &&
    phoneNumberInput.keyboard.length &&
    phoneNumberInput.selectedCountry &&
    phoneNumberInput.selectedCountry.size &&
    phoneNumberInput.keyboard.length === phoneNumberInput.selectedCountry.size;

  const classes = useStyles({ fontFamily })();

  return (
    <>
      <Grid className={classes.outerContainer}>
        <Grid className={classes.deliveryInput}>
          <Box flexOverride="none" margin="6">
            <Typography fontSize={72} fontWeight={800} align="center">
              {headerText}
            </Typography>
          </Box>
          <Typography className={classes.deliverySubHeading}>{subheaderText}</Typography>
          <Grid className={classes.deliveryInputBoxPhoneNumber}>
            <PhoneNumberInput
              textInputProps={{
                autoFocus: true,
                name: 'value',
                fullWidth: true,
                style: { minHeight: '68px' }
              }}
              inputModel={phoneNumberInput}
              setPhoneNumber={(phNum, number) => handlePhoneNumber(phNum, number)}
            />
          </Grid>
        </Grid>
      </Grid>

      <AbsoluteBox raised fullWidth bg="x-light-gray" bottom="0" left="0">
        <Grid>
          <NumPadAction
            onClick={handleKeyboardClick}
            isConfirmDisabled={!isValidPhoneNumber ? true : false}
            onConfirm={() => onNext('textMessage', phoneNumber)}
            onBack={onPrev}
            layout="row"
            confirmConfig={{
              label: nextButtonText,
              backLabel: backButtonText,
              customSize: 'x-large',
              width: 475,
              appearance: 'primary'
            }}
          />
        </Grid>
      </AbsoluteBox>
    </>
  );
};
